/**
 * useStaffAvailability — one person's RECURRING weekly pattern, read and
 * written through the typed client (`getStaffAvailability` /
 * `setStaffAvailability`, contract 2026-09-11).
 *
 * No seed data in the UI, and no local-only edit: save PUTs the whole set and
 * re-renders from the response, so a rejected write shows as a failure rather
 * than as a row that quietly disappears on reload. `timezone` and `seeded`
 * ride out of the hook untouched — the screen has to be able to say "stated in
 * Europe/Zurich" and to tell "no pattern recorded yet" from "no fixed hours".
 */
import { useCallback, useEffect, useState } from "react";
import { getStaffAvailability, setStaffAvailability } from "@v0y4ge/api-client";
import type { StaffAvailabilityEnvelope, StaffAvailabilityRule } from "@v0y4ge/api-contracts";

export type AvailabilityRule = StaffAvailabilityRule;
export const WEEKDAYS: AvailabilityRule["day"][] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const WEEKDAY_LABEL: Record<string, string> = {
  Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday",
  Fri: "Friday", Sat: "Saturday", Sun: "Sunday",
};

export interface StaffAvailabilityState {
  rules: AvailabilityRule[];
  timezone: string | null;
  /** False means nobody has recorded a pattern for this person yet. */
  seeded: boolean;
  loading: boolean;
  /** Set only when the READ failed — never for an empty pattern. */
  error: string | null;
  saving: boolean;
  /** Replaces the whole set. Resolves true on a saved write. */
  save: (next: AvailabilityRule[]) => Promise<boolean>;
  /** Surfaced so the caller can show the reason a save was refused. */
  saveError: string | null;
  reload: () => void;
}

export function useStaffAvailability(staffId: string | null): StaffAvailabilityState {
  const [env, setEnv] = useState<StaffAvailabilityEnvelope | null>(null);
  const [loading, setLoading] = useState(!!staffId);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    if (!staffId) { setEnv(null); setLoading(false); setError(null); return; }
    setLoading(true);
    setError(null);
    getStaffAvailability(staffId)
      .then((e) => { if (live) { setEnv(e); setLoading(false); } })
      .catch((e: unknown) => {
        if (!live) return;
        console.warn("getStaffAvailability failed:", e);
        setError("Couldn't load this person's availability.");
        setLoading(false);
      });
    return () => { live = false; };
  }, [staffId, nonce]);

  const save = useCallback(async (next: AvailabilityRule[]) => {
    if (!staffId) return false;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await setStaffAvailability(staffId, next);
      setEnv(saved);
      return true;
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [staffId]);

  return {
    rules: env?.rules ?? [],
    timezone: env?.timezone ?? null,
    seeded: env?.seeded ?? false,
    loading,
    error,
    saving,
    save,
    saveError,
    reload: () => setNonce((n) => n + 1),
  };
}
